import { makeAutoObservable, runInAction } from 'mobx'
import {
  IntegrationKey,
  type Integration,
  type ISalesForceSettingsDict,
} from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { SettingsPropertyItemType } from 'pages/settings/pages/integrations/pages/integrationSettings/model/types'
import type { IntegrationCustomSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationCustomSettingsStore'
import { SettingsProperty } from 'pages/settings/pages/integrations/pages/integrationSettings/model/SettingsProperty'
import { IntegrationSettingsCustomPropertyStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationSettingsCustomPropertyStore'
import {
  SALESFORCE_CONDITIONAL_PROPERTIES,
  SALESFORCE_PROPERTIES,
  SALESFORCE_BANNED_IDS,
} from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSalesforceSettings/lib'
import { type IntegrationSalesforceSettingsInstallationStore } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSalesforceSettings/store/IntegrationSalesforceSettingsInstallationStore'

export class IntegrationSalesforceSettingsPropertyStore {
  private _properties = new Map<
    number | string,
    SettingsProperty<string, ISalesForceSettingsDict>
  >()
  private _customPropertyStore

  constructor(
    private _integration: Integration,
    private _customSetting: IntegrationCustomSettingsStore<ISalesForceSettingsDict>,
    private _installationStore: IntegrationSalesforceSettingsInstallationStore,
    private _entities: IEntitiesStore
  ) {
    this._customPropertyStore = new IntegrationSettingsCustomPropertyStore<ISalesForceSettingsDict>(
      _integration,
      _customSetting,
      SALESFORCE_BANNED_IDS,
      this._entities
    )
    makeAutoObservable(this)
  }

  async init() {
    const integration = this._entities.integrationsStore.getIntegration(IntegrationKey.salesforce)
    if (!integration?.isConnected) return

    const customSettingTimeout = setInterval(() => {
      if (this._customSetting.status.isSuccess) {
        clearInterval(customSettingTimeout)
        this._defaultFieldsInitialization()
      }
    }, 200)
    this._customPropertyStore.init()
  }

  private _defaultFieldsInitialization = () => {
    this._properties.clear()

    this._fieldsInitialization(SALESFORCE_PROPERTIES)
    if (!this.isLimitedIntegration) {
      this._fieldsInitialization(SALESFORCE_CONDITIONAL_PROPERTIES)
    }
    this._disableFlexFields()
  }

  private _fieldsInitialization = (
    fields: SettingsPropertyItemType<string, ISalesForceSettingsDict>[]
  ) => {
    fields.forEach((item) => {
      let property: SettingsPropertyItemType<string, ISalesForceSettingsDict> = item

      if (property.customSettingId) {
        property = {
          ...property,
          integrationName: this.integrationName,
          value: this._customSetting.getValueOrFirstOption(property.customSettingId),
          options: this._customSetting.getOptions(property.customSettingId)?.map((item) => ({
            value: item.value,
            label: item.label,
            id: item.value,
            name: item.value,
          })),
        }
      }
      this._setProperty(property)
    })
  }

  private _refreshStore = () => {
    this._defaultFieldsInitialization()
    this._customPropertyStore.refreshStore()
  }

  private _patchData = async (
    newData: SettingsPropertyItemType<string, ISalesForceSettingsDict>
  ) => {
    const item = this._properties.get(newData.id)
    if (!item) return

    this._setProperty(newData)

    if (newData.customSettingId) {
      const response = await this._customSetting.patchData([
        {
          key: newData.customSettingId,
          value: String(newData.value),
        },
      ])

      if (!(response?.status === 200)) {
        this._setProperty(item)
      }
    }
  }

  private _setProperty = (data: SettingsPropertyItemType<string, ISalesForceSettingsDict>) => {
    runInAction(() => {
      this._properties.set(
        data.id,
        new SettingsProperty<string, ISalesForceSettingsDict>(data, {
          onChange: this._patchData,
        })
      )
    })
  }

  private _disableFlexFields = () => {
    const leadPhone = this._properties.get('lead_custom_phone_number')
    const contactPhone = this._properties.get('contact_custom_phone_number')
    if (!leadPhone || !contactPhone) return

    if (this.numberEnabled) {
      this._setProperty({ ...leadPhone.original, disabled: false })
      this._setProperty({ ...contactPhone.original, disabled: false })
    } else {
      this._setProperty({ ...leadPhone.original, disabled: true })
      this._setProperty({ ...contactPhone.original, disabled: true })
    }
  }

  setNumberEnabled = (checked: boolean) => {
    if (checked) {
      this._customSetting.patchData([
        {
          key: 'is_custom_phone_number_enabled',
          value: 1,
        },
        {
          key: 'lead_custom_phone_number',
          value: this._customSetting.getValueOrFirstOption('lead_custom_phone_number'),
        },
        {
          key: 'contact_custom_phone_number',
          value: this._customSetting.getValueOrFirstOption('contact_custom_phone_number'),
        },
      ])
    } else {
      this._customSetting.patchData([
        {
          key: 'is_custom_phone_number_enabled',
          value: 0,
        },
      ])
    }
    this._disableFlexFields()
  }

  saveNewProperties = async () => {
    this._customPropertyStore.saveNewProperties()
  }

  clearNewProperties = () => {
    this._refreshStore()
  }

  get numberEnabled() {
    return Boolean(this._customSetting.getValue('is_custom_phone_number_enabled'))
  }

  get integrationName() {
    return this._integration?.name || ''
  }

  get rows() {
    return Array.from(this._properties.values()).concat(this._customPropertyStore.rows)
  }

  get showSaveControl() {
    if (this.customFieldsLoading) {
      return false
    }

    return (
      this._customPropertyStore.isExistNewProperties ||
      this._customPropertyStore.isRemovedOldProperties
    )
  }

  get mappedLength() {
    return this._properties.size + this._customPropertyStore.mappedLength
  }

  get customFieldsLoading() {
    return this._customPropertyStore.loading
  }

  get loading() {
    return this._customSetting.status.isLoading
  }

  get customPropertyStore() {
    return this._customPropertyStore
  }

  get isLimitedIntegration() {
    // TODO: temporary integration stage
    // return this._installationStore.isLimitedIntegration
    return false
  }
}
